import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { ResultsActions } from './results.actions';
import { ResultsApiMapper } from './results-api.mapper';
import { SystemComparisonControllerService, VariantsControllerService } from '@nx-customer-apps/api-planning-projects-v2';
import { GlobalSpinnerService } from '@nx-customer-apps/components';

@Injectable({ providedIn: 'root' })
export class ResultsEffects {
    private actions$ = inject(Actions);
    private router = inject(Router);
    private resultsApiMapper = inject(ResultsApiMapper);
    private globalSpinnerService = inject(GlobalSpinnerService);
    private variantsControllerService = inject(VariantsControllerService);
    private systemComparisonControllerService = inject(SystemComparisonControllerService);

    public calculateOptimalSystem$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ResultsActions.calculateOptimalSystem),
            tap(() => this.globalSpinnerService.next({ isShown: true, loadingText: 'LOADER.TEXT.OPTIMAL_SYSTEM' })),
            switchMap(payload => {
                const body = this.resultsApiMapper.mapToOptimalSystemRequestDTO(payload);

                return this.variantsControllerService.variantsControllerCreateOptimalVariant({ body }).pipe(
                    map(response => {
                        const modelResponse = this.resultsApiMapper.mapToOptimalSystemResponseModel(response);

                        return ResultsActions.calculateOptimalSystemSuccess(modelResponse);
                    }),
                    catchError(error => of(ResultsActions.calculateSystemFailure(error)))
                );
            })
        )
    );

    public calculateSystemComparison$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ResultsActions.calculateOptimalSystemSuccess),
            tap(() => this.globalSpinnerService.next({ isShown: true, loadingText: 'LOADER.TEXT.SYSTEM_COMPARISON' })),
            switchMap(payload => {
                const body = this.resultsApiMapper.mapToSystemComparisonRequestDTO(payload);

                return this.systemComparisonControllerService
                    .systemComparisonControllerPostSystemComparison({ id: payload.projectId!, body })
                    .pipe(
                        map(response => {
                            const modelResponse = this.resultsApiMapper.mapToSystemComparisonResponseModel(response);

                            return ResultsActions.calculateSystemComparisonSuccess(modelResponse);
                        }),
                        catchError(error => of(ResultsActions.calculateSystemFailure(error)))
                    );
            })
        )
    );

    public calculateSystemComparisonSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ResultsActions.calculateSystemComparisonSuccess),
                tap(() => {
                    this.globalSpinnerService.reset();
                    this.router.navigate(['/results']);
                })
            ),
        { dispatch: false }
    );

    public calculateSystemFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ResultsActions.calculateSystemFailure),
                tap(error => {
                    console.error('[RESULTS] calculateSystem failed', error);
                    this.globalSpinnerService.reset();
                })
            ),
        { dispatch: false }
    );
}
